import {
  Constants,
  DEFAULT_SUPPORT_EMAIL,
} from "../../../domain/models/Constants";
import { CoreError, CoreErrorCause } from "../../../domain/models/CoreError";

export class ConstantsConverter {
  private static parseOptionalProbability(value: unknown): number | undefined {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return undefined;
    }

    return value >= 0 && value <= 1 ? value : undefined;
  }

  private static parseOptionalPositiveNumber(
    value: unknown,
  ): number | undefined {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return undefined;
    }

    return value > 0 ? value : undefined;
  }

  private static parseOptionalNonNegativeInteger(
    value: unknown,
  ): number | undefined {
    if (typeof value !== "number" || !Number.isInteger(value)) {
      return undefined;
    }

    return value >= 0 ? value : undefined;
  }

  /**
   * Converts Firebase Realtime Database snapshot data to Constants domain model.
   * Parses all required constants fields and validates the data structure.
   *
   * @param data The Firebase snapshot data from constants/
   * @returns Constants object
   * @throws CoreError if parsing fails
   */
  static domainModel(data: Record<string, any> | null | undefined): Constants {
    if (!data) {
      throw new CoreError({
        cause: CoreErrorCause.ConstantsCouldNotBeParsed,
        context: "Constants snapshot does not exist",
      });
    }

    const errors: string[] = [];

    // Parse numberOfCardsPerBatch
    const numberOfCardsPerBatch = data.numberOfCardsPerBatch;
    if (typeof numberOfCardsPerBatch !== "number") {
      errors.push("numberOfCardsPerBatch is missing or invalid");
    }

    // Parse secondsToTriggerLocalNotification
    const secondsToTriggerLocalNotification =
      data.secondsToTriggerLocalNotification;
    if (typeof secondsToTriggerLocalNotification !== "number") {
      errors.push("secondsToTriggerLocalNotification is missing or invalid");
    }

    // Parse secondsToReleaseNewCard
    const secondsToReleaseNewCard = data.secondsToReleaseNewCard;
    if (typeof secondsToReleaseNewCard !== "number") {
      errors.push("secondsToReleaseNewCard is missing or invalid");
    }

    // Optional: numberOfCardsToLoadImages
    const numberOfCardsToLoadImages =
      typeof data.numberOfCardsToLoadImages === "number"
        ? data.numberOfCardsToLoadImages
        : undefined;
    const randomSuperlikePaywallProbability = this.parseOptionalProbability(
      data.randomSuperlikePaywallProbability,
    );

    const supportEmail =
      typeof data.supportEmail === "string" &&
      data.supportEmail.trim().length > 0
        ? data.supportEmail.trim()
        : DEFAULT_SUPPORT_EMAIL;

    // Parse numberOfDigitsInInvitationCode
    const numberOfDigitsInInvitationCode = data.numberOfDigitsInInvitationCode;
    if (typeof numberOfDigitsInInvitationCode !== "number") {
      errors.push("numberOfDigitsInInvitationCode is missing or invalid");
    }

    // Parse lastCompatibleVersion
    const lastCompatibleVersion = data.lastCompatibleVersion;
    if (typeof lastCompatibleVersion !== "string") {
      errors.push("lastCompatibleVersion is missing or invalid");
    }

    // If any errors occurred, throw
    if (errors.length > 0) {
      throw new CoreError({
        cause: CoreErrorCause.ConstantsCouldNotBeParsed,
        context: `Failed to parse constants: ${errors.join(", ")}`,
      });
    }

    // Parse version string into components
    const versionNumbers = lastCompatibleVersion.split(".");
    if (versionNumbers.length !== 3) {
      throw new CoreError({
        cause: CoreErrorCause.ConstantsCouldNotBeParsed,
        context: 'lastCompatibleVersion must be in format "X.Y.Z"',
      });
    }

    const lastCompatibleMainVersionNumber = parseInt(versionNumbers[0], 10);
    const lastCompatibleMinorVersionNumber = parseInt(versionNumbers[1], 10);
    const lastCompatibleBuildNumber = parseInt(versionNumbers[2], 10);

    if (
      isNaN(lastCompatibleMainVersionNumber) ||
      isNaN(lastCompatibleMinorVersionNumber) ||
      isNaN(lastCompatibleBuildNumber)
    ) {
      throw new CoreError({
        cause: CoreErrorCause.ConstantsCouldNotBeParsed,
        context: "lastCompatibleVersion contains invalid numbers",
      });
    }

    // Optional fictional-character tuning (fail-open on invalid/missing values)
    const characterInstantMatchProbabilityMin = this.parseOptionalProbability(
      data.characterInstantMatchProbabilityMin,
    );
    const characterInstantMatchProbabilityMax = this.parseOptionalProbability(
      data.characterInstantMatchProbabilityMax,
    );
    const characterPlayDelayMinSeconds = this.parseOptionalPositiveNumber(
      data.characterPlayDelayMinSeconds,
    );
    const characterPlayDelayMaxSeconds = this.parseOptionalPositiveNumber(
      data.characterPlayDelayMaxSeconds,
    );
    const characterRevealStaggerMinSeconds = this.parseOptionalPositiveNumber(
      data.characterRevealStaggerMinSeconds,
    );
    const characterRevealStaggerMaxSeconds = this.parseOptionalPositiveNumber(
      data.characterRevealStaggerMaxSeconds,
    );

    // Strict boolean parse: absent or malformed means "do not sell".
    const allCardsCoupleUnlockSellingEnabled =
      data.allCardsCoupleUnlockSellingEnabled === true;
    const allCardsCharacterUnlockSellingEnabled =
      data.allCardsCharacterUnlockSellingEnabled === true;
    const allCardsSoloUnlockSellingEnabled =
      data.allCardsSoloUnlockSellingEnabled === true;
    // Absent or malformed falls back to the domain default threshold.
    const minRemainingCardsForAllCardsUnlock =
      this.parseOptionalNonNegativeInteger(
        data.minRemainingCardsForAllCardsUnlock,
      );

    return {
      numberOfCardsPerBatch,
      secondsToTriggerLocalNotification,
      secondsToReleaseNewCard,
      randomSuperlikePaywallProbability,
      numberOfCardsToLoadImages,
      supportEmail,
      numberOfDigitsInInvitationCode,
      lastCompatibleVersion,
      lastCompatibleMainVersionNumber,
      lastCompatibleMinorVersionNumber,
      lastCompatibleBuildNumber,
      characterInstantMatchProbabilityMin,
      characterInstantMatchProbabilityMax,
      characterPlayDelayMinSeconds,
      characterPlayDelayMaxSeconds,
      characterRevealStaggerMinSeconds,
      characterRevealStaggerMaxSeconds,
      allCardsCoupleUnlockSellingEnabled,
      allCardsCharacterUnlockSellingEnabled,
      allCardsSoloUnlockSellingEnabled,
      minRemainingCardsForAllCardsUnlock,
    };
  }
}
